"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";

// ─── data ────────────────────────────────────────────────────────────────────

const images = [
  { src: "/images/products-carousel/carousel-1.jpg", alt: "Producto 1" },
  { src: "/images/products-carousel/carousel-2.jpg", alt: "Producto 2" },
  { src: "/images/products-carousel/carousel-3.jpg", alt: "Producto 3" },
  { src: "/images/products-carousel/carousel-4.jpg", alt: "Producto 4" },
  { src: "/images/products-carousel/carousel-5.jpg", alt: "Producto 5" },
  { src: "/images/products-carousel/carousel-6.jpg", alt: "Producto 6" },
  { src: "/images/products-carousel/carousel-7.jpg", alt: "Producto 7" },
  { src: "/images/products-carousel/carousel-8.jpg", alt: "Producto 8" },
  { src: "/images/products-carousel/carousel-9.jpg", alt: "Producto 9" },
];

// Height/width pattern cycling across the 9 images (fixed px — no clamp needed)
const SHAPES = [
  { h: 420, w: 240 },
  { h: 420, w: 240 },
  { h: 420, w: 240 },
  { h: 420, w: 240 },
  { h: 420, w: 240 },
];

const SPEED = 110; // px/s
const GAP   = 8;   // px between items and between strips

// ─── strip ───────────────────────────────────────────────────────────────────

function Strip({
  id,
  refProp,
}: {
  id: string;
  refProp?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div
      ref={refProp}
      aria-hidden={id !== "main" ? "true" : undefined}
      className="flex shrink-0 items-end"
      style={{ gap: GAP }}
    >
      {images.map((img, i) => {
        const s = SHAPES[i % SHAPES.length];
        return (
          <div
            key={`${id}-${i}`}
            className="shrink-0 overflow-hidden rounded-[2px]"
            style={{ width: s.w, height: s.h }}
          >
            <Image
              src={img.src}
              alt={id === "main" ? img.alt : ""}
              width={s.w}
              height={s.h}
              quality={90}
              priority={id === "main" && i < 2}
              loading={id === "main" && i < 4 ? "eager" : "lazy"}
              decoding="async"
              sizes={`${s.w * 2}px`}
              className="h-full w-full object-cover"
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

export default function ProductsCarousel() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef   = useRef<HTMLDivElement>(null);
  const strip1Ref  = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track  = trackRef.current;
    const strip1 = strip1Ref.current;
    if (!track || !strip1) return;

    // Respect OS reduced-motion preference — show static, no animation
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // ── 1. Inject keyframes once ───────────────────────────────────────────
    const STYLE_ID = "pc-keyframes";
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent =
        "@keyframes pc-marquee{" +
        "from{transform:translate3d(0,0,0)}" +
        "to{transform:translate3d(var(--pc-dist),0,0)}" +
        "}";
      document.head.appendChild(style);
    }

    const applyAnimation = () => {
      const loopDist = strip1.offsetWidth + GAP;
      const duration = loopDist / SPEED; // seconds
      track.style.setProperty("--pc-dist", `-${loopDist}px`);
      track.style.animation = `pc-marquee ${duration}s linear infinite`;
    };

    // First paint: measure after images/layout render
    const rafId = requestAnimationFrame(applyAnimation);

    // Recompute when images decode / viewport changes
    const ro = new ResizeObserver(applyAnimation);
    ro.observe(strip1);

    // ── 4. Pause when tab is hidden (saves GPU) ────────────────────────────
    const onVisibility = () => {
      track.style.animationPlayState = document.hidden ? "paused" : "running";
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ── 5. Pause when section is off-screen (saves GPU) ────────────────────
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => {
          // Only touch play state if tab is visible
          if (!document.hidden) {
            track.style.animationPlayState = entry.isIntersecting
              ? "running"
              : "paused";
          }
        },
        { threshold: 0 }
      );
      const wrapper = wrapperRef.current;
      if (wrapper) io.observe(wrapper);
    }

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      track.style.animation = "";
      track.style.removeProperty("--pc-dist");
      document.removeEventListener("visibilitychange", onVisibility);
      io?.disconnect();
    };
  }, []);

  return (
    <section id="catalogo" className="bg-white pb-32">
        <div className="px-[clamp(16px,2.7vw,52px)] pb-20">
        <h2 className="text-[clamp(28px,6vw,80px)] leading-[1.05] font-[800] text-[color:var(--color-black)]">
          Catálogo<span className="text-[color:var(--color-red)]">.</span>
        </h2>
        <p className="mt-[clamp(10px,1.6vw,28px)] max-w-[clamp(280px,70vw,980px)] text-[clamp(14px,3.5vw,30px)] leading-[1.15] font-medium uppercase text-[color:var(--color-black)]">
          ELEGÍ PRODUCTOS QUE SE USAN TODOS LOS DÍAS. PERSONALIZALAS. HACE QUE TU MARCA DEJE HUELLA.
        </p>
      </div>
      <div ref={wrapperRef} className="relative overflow-hidden">
        <div
          ref={trackRef}
          className="flex items-end will-change-transform"
          style={{ gap: GAP }}
        >
          <Strip id="main"  refProp={strip1Ref} />
          <Strip id="ghost" />
        </div>
      </div>
    </section>
  );
}
